import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Check, ChevronDown, Loader2, CheckCheck, Package } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { productsAPI, categoriesAPI, brandsAPI } from '@/services/api';

/**
 * ServerProductMultiSelect — opens a large Dialog for a comfortable multi-select
 * experience. Debounced server-side search, Category/Brand filter chips, and a
 * stable list that doesn't reset scroll when toggling items.
 */
const PAGE_SIZE = 100;

export default function ServerProductMultiSelect({
  selectedIds = [],
  onChange,
  placeholder = 'Select products...',
  searchPlaceholder = 'Search by name or SKU...',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCache, setSelectedCache] = useState({}); // id -> product
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [filterCategoryIds, setFilterCategoryIds] = useState([]);
  const [filterBrandIds, setFilterBrandIds] = useState([]);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const scrollRef = useRef(null);
  const scrollTopRef = useRef(0); // preserve scroll position across toggles

  // Fetch filter options once
  useEffect(() => {
    (async () => {
      try {
        const [cRes, bRes] = await Promise.all([
          categoriesAPI.list({ is_active: true }),
          brandsAPI.list({ is_active: true }),
        ]);
        setCategories(cRes.data || []);
        setBrands(bRes.data || []);
      } catch (e) { /* non-fatal */ }
    })();
  }, []);

  // Debounce query
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  const fetchResults = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const params = { page: 1, page_size: PAGE_SIZE, is_active: true };
      if (debouncedQuery) params.search = debouncedQuery;
      if (filterCategoryIds.length === 1) params.category_id = filterCategoryIds[0];
      if (filterBrandIds.length === 1) params.brand_id = filterBrandIds[0];
      const res = await productsAPI.list(params);
      let items = res.data?.items || [];
      if (filterCategoryIds.length > 1) items = items.filter(p => filterCategoryIds.includes(p.category_id));
      if (filterBrandIds.length > 1) items = items.filter(p => filterBrandIds.includes(p.brand_id));
      setResults(items);
      setFilteredTotal(res.data?.total || items.length);
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery, filterCategoryIds, filterBrandIds, open]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  // Restore scroll position after any re-render that resets it
  useEffect(() => {
    if (scrollRef.current && scrollTopRef.current > 0) {
      scrollRef.current.scrollTop = scrollTopRef.current;
    }
  });

  const onListScroll = () => {
    if (scrollRef.current) scrollTopRef.current = scrollRef.current.scrollTop;
  };

  // Prefetch names of selected IDs that aren't in cache yet (chunked)
  useEffect(() => {
    const missing = selectedIds.filter(id => !selectedCache[id]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const fetched = await Promise.all(
          missing.slice(0, 50).map(id => productsAPI.get(id).then(r => r.data).catch(() => null))
        );
        if (cancelled) return;
        setSelectedCache(prev => {
          const next = { ...prev };
          fetched.filter(Boolean).forEach(p => { next[p.id] = p; });
          return next;
        });
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [selectedIds]); // eslint-disable-line

  const toggleProduct = (p) => {
    setSelectedCache(prev => ({ ...prev, [p.id]: p }));
    const set = new Set(selectedIds);
    set.has(p.id) ? set.delete(p.id) : set.add(p.id);
    onChange(Array.from(set));
  };

  const selectAllFiltered = () => {
    const set = new Set(selectedIds);
    const cache = { ...selectedCache };
    results.forEach(p => { set.add(p.id); cache[p.id] = p; });
    setSelectedCache(cache);
    onChange(Array.from(set));
  };

  const deselectAllFiltered = () => {
    const set = new Set(selectedIds);
    results.forEach(p => set.delete(p.id));
    onChange(Array.from(set));
  };

  const clearAll = (e) => { e?.stopPropagation(); onChange([]); };
  const removeOne = (id, e) => { e.stopPropagation(); onChange(selectedIds.filter(i => i !== id)); };

  const toggleCategoryFilter = (id) => {
    setFilterCategoryIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    scrollTopRef.current = 0; // reset scroll on filter change
  };
  const toggleBrandFilter = (id) => {
    setFilterBrandIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    scrollTopRef.current = 0;
  };

  const selectedProducts = selectedIds.map(id => selectedCache[id]).filter(Boolean);
  const allShownSelected = results.length > 0 && results.every(p => selectedIds.includes(p.id));

  return (
    <>
      {/* Trigger / selected tags */}
      <div
        className={cn(
          'min-h-[42px] p-2 border rounded-lg transition-colors bg-white',
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'hover:border-gray-400 cursor-pointer',
          open ? 'border-gray-400 ring-2 ring-gray-200' : 'border-gray-300'
        )}
        onClick={() => !disabled && setOpen(true)}
        data-testid="server-product-multiselect"
      >
        {selectedIds.length === 0 ? (
          <div className="flex items-center justify-between text-gray-500 px-1">
            <span>{placeholder}</span>
            <ChevronDown className="w-4 h-4" />
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5 items-center">
            {selectedProducts.slice(0, 8).map(p => (
              <Badge key={p.id} className="bg-orange-100 text-orange-700 flex items-center gap-1 pr-1">
                <span className="max-w-[160px] truncate">{p.name}</span>
                <button type="button" onClick={(e) => removeOne(p.id, e)} className="ml-1 rounded-full p-0.5 hover:bg-black/10">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
            {selectedIds.length > selectedProducts.length && (
              <Badge variant="secondary" className="bg-gray-200 text-gray-700">
                {selectedIds.length - selectedProducts.length} more loading…
              </Badge>
            )}
            {selectedProducts.length > 8 && (
              <Badge variant="secondary" className="bg-gray-200 text-gray-700">
                +{selectedProducts.length - 8} more
              </Badge>
            )}
            <button type="button" onClick={clearAll} className="ml-auto text-xs text-gray-500 hover:text-gray-700 px-2">
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Big dialog picker */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl w-[95vw] h-[85vh] p-0 gap-0 flex flex-col">
          <DialogHeader className="px-6 py-4 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-orange-600" />
              Select Products
              <span className="text-sm font-normal text-gray-500 ml-2">
                {selectedIds.length} selected
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-12 gap-0 flex-1 min-h-0">
            {/* Left: Filters */}
            <div className="col-span-4 xl:col-span-3 border-r overflow-y-auto p-4 space-y-4 bg-gray-50/50">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-700">Categories</h3>
                  {filterCategoryIds.length > 0 && (
                    <button type="button" onClick={() => { setFilterCategoryIds([]); scrollTopRef.current = 0; }} className="text-xs text-gray-500 hover:text-gray-700">
                      Clear
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {categories.length === 0 && <span className="text-xs text-gray-400">No categories</span>}
                  {categories.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleCategoryFilter(c.id)}
                      className={cn(
                        'text-xs px-2.5 py-1 rounded-full border transition-colors',
                        filterCategoryIds.includes(c.id)
                          ? 'bg-blue-100 border-blue-400 text-blue-700 font-medium'
                          : 'bg-white border-gray-200 hover:border-gray-400 text-gray-600'
                      )}
                      data-testid={`cat-filter-${c.id}`}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-700">Brands</h3>
                  {filterBrandIds.length > 0 && (
                    <button type="button" onClick={() => { setFilterBrandIds([]); scrollTopRef.current = 0; }} className="text-xs text-gray-500 hover:text-gray-700">
                      Clear
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {brands.length === 0 && <span className="text-xs text-gray-400">No brands</span>}
                  {brands.map(b => (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => toggleBrandFilter(b.id)}
                      className={cn(
                        'text-xs px-2.5 py-1 rounded-full border transition-colors',
                        filterBrandIds.includes(b.id)
                          ? 'bg-purple-100 border-purple-400 text-purple-700 font-medium'
                          : 'bg-white border-gray-200 hover:border-gray-400 text-gray-600'
                      )}
                      data-testid={`brand-filter-${b.id}`}
                    >
                      {b.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Search + list */}
            <div className="col-span-8 xl:col-span-9 flex flex-col min-h-0">
              <div className="px-4 py-3 border-b space-y-2 shrink-0">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    autoFocus
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); scrollTopRef.current = 0; }}
                    placeholder={searchPlaceholder}
                    className="pl-9 h-10 bg-white border-gray-300"
                    data-testid="product-search-input"
                  />
                  {query && (
                    <button type="button" onClick={() => { setQuery(''); scrollTopRef.current = 0; }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">
                    {loading ? 'Loading…' : `${results.length} shown · ${filteredTotal} total`}
                  </span>
                  {results.length > 0 && (
                    <Button
                      size="sm"
                      variant={allShownSelected ? 'outline' : 'default'}
                      onClick={allShownSelected ? deselectAllFiltered : selectAllFiltered}
                      className={allShownSelected ? 'h-8' : 'h-8 bg-orange-600 hover:bg-orange-700'}
                      data-testid="select-all-filtered"
                    >
                      <CheckCheck className="w-3.5 h-3.5 mr-1" />
                      {allShownSelected ? `Deselect ${results.length} shown` : `Select all ${results.length} shown`}
                    </Button>
                  )}
                </div>
              </div>

              <div
                ref={scrollRef}
                onScroll={onListScroll}
                className="flex-1 overflow-y-auto p-2"
                data-testid="product-list-scroller"
              >
                {loading && results.length === 0 && (
                  <div className="py-10 text-center text-gray-400 text-sm flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                  </div>
                )}
                {!loading && results.length === 0 && (
                  <div className="py-10 text-center text-gray-500 text-sm">
                    No products match the current filters.
                  </div>
                )}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-1.5">
                  {results.map(p => {
                    const isSelected = selectedIds.includes(p.id);
                    return (
                      <div
                        key={p.id}
                        onClick={() => toggleProduct(p)}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer transition-colors border',
                          isSelected ? 'bg-orange-50 border-orange-300' : 'bg-white border-gray-100 hover:bg-gray-50 hover:border-gray-200'
                        )}
                        data-testid={`product-row-${p.id}`}
                      >
                        <div className={cn(
                          'w-5 h-5 rounded border-2 flex items-center justify-center shrink-0',
                          isSelected ? 'bg-orange-500 border-orange-500 text-white' : 'border-gray-300'
                        )}>
                          {isSelected && <Check className="w-3 h-3" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-800 truncate text-sm">{p.name}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {p.sku}
                            {(p.selling_price || p.current_price?.selling_price) && (
                              <span> · ₹{Math.round(Number(p.selling_price || p.current_price?.selling_price)).toLocaleString('en-IN')}</span>
                            )}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-3 border-t shrink-0 bg-gray-50/50">
            <div className="flex w-full items-center justify-between">
              <span className="text-sm text-gray-600">
                <strong>{selectedIds.length}</strong> product{selectedIds.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={clearAll} disabled={selectedIds.length === 0}>
                  Clear all
                </Button>
                <Button onClick={() => setOpen(false)} className="bg-orange-600 hover:bg-orange-700" data-testid="picker-done">
                  Done
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
